import { MAP, CAR, PHYSICS, AIRBORNE, PHYSICS_FEEL } from '../utils/constants';
import { BUILDING_LOT_PAD, isParkLot } from './mapLayout';

export type AABBXZ = {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
};

let cachedBuildings: AABBXZ[] | null = null;

/** Call after changing park-lot rules (e.g. hot reload) so collision matches mapLayout. */
export function invalidateBuildingAABBCache(): void {
  cachedBuildings = null;
}

/** Building footprints (park lots omitted). Collision box matches visual buildings. */
export function getBuildingAABBs(): AABBXZ[] {
  if (cachedBuildings) return cachedBuildings;
  const { HALF, GRID, ROAD_WIDTH, SIDEWALK_WIDTH } = MAP;
  const roadWithSidewalk = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
  const totalRoadSpace = (GRID + 1) * roadWithSidewalk;
  const inner = MAP.SIZE - totalRoadSpace;
  const blockW = inner / GRID;
  const halfW = (blockW - BUILDING_LOT_PAD) * 0.5;
  const boxes: AABBXZ[] = [];
  for (let iz = 0; iz < GRID; iz++) {
    for (let ix = 0; ix < GRID; ix++) {
      if (isParkLot(ix, iz)) continue;
      const cx =
        -HALF + roadWithSidewalk + blockW / 2 + ix * (blockW + roadWithSidewalk);
      const cz =
        -HALF + roadWithSidewalk + blockW / 2 + iz * (blockW + roadWithSidewalk);
      boxes.push({
        minX: cx - halfW,
        maxX: cx + halfW,
        minZ: cz - halfW,
        maxZ: cz + halfW,
      });
    }
  }
  cachedBuildings = boxes;
  return boxes;
}

export type CircleResolve = {
  x: number;
  z: number;
  nx: number;
  nz: number;
};

/** Push circle out of AABB; returns null if no overlap. */
export function resolveCircleAABB(
  x: number,
  z: number,
  r: number,
  box: AABBXZ,
): CircleResolve | null {
  const px = Math.max(box.minX, Math.min(x, box.maxX));
  const pz = Math.max(box.minZ, Math.min(z, box.maxZ));
  let dx = x - px;
  let dz = z - pz;
  const d2 = dx * dx + dz * dz;
  if (d2 >= r * r) return null;

  if (d2 < 1e-10) {
    const dxL = x - box.minX;
    const dxR = box.maxX - x;
    const dzB = z - box.minZ;
    const dzT = box.maxZ - z;
    const m = Math.min(dxL, dxR, dzB, dzT);
    if (m === dxL) return { x: box.minX - r, z, nx: -1, nz: 0 };
    if (m === dxR) return { x: box.maxX + r, z, nx: 1, nz: 0 };
    if (m === dzB) return { x, z: box.minZ - r, nx: 0, nz: -1 };
    return { x, z: box.maxZ + r, nx: 0, nz: 1 };
  }

  const d = Math.sqrt(d2);
  const nx = dx / d;
  const nz = dz / d;
  const pen = r - d;
  return { x: x + nx * pen, z: z + nz * pen, nx, nz };
}

function reflectVelocity(
  vx: number,
  vz: number,
  nx: number,
  nz: number,
  restitution: number,
): { vx: number; vz: number } {
  const dot = vx * nx + vz * nz;
  if (dot >= 0) return { vx, vz };
  const outX = vx - 2 * dot * nx;
  const outZ = vz - 2 * dot * nz;
  return { vx: outX * restitution, vz: outZ * restitution };
}

function wallScrubVelocity(
  vx: number,
  vz: number,
  nx: number,
  nz: number,
): { vx: number; vz: number; scraping: boolean } {
  const { WALL_SCRUB_FRICTION, WALL_SCRUB_SPEED_LOSS } = PHYSICS_FEEL;
  
  const normalDot = vx * nx + vz * nz;
  if (normalDot >= 0) return { vx, vz, scraping: false };

  const tangentX = vx - normalDot * nx;
  const tangentZ = vz - normalDot * nz;

  const scrubX = tangentX * WALL_SCRUB_FRICTION;
  const scrubZ = tangentZ * WALL_SCRUB_FRICTION;

  const speed = Math.hypot(scrubX, scrubZ);
  const speedLoss = speed * WALL_SCRUB_SPEED_LOSS;
  const newSpeed = Math.max(0, speed - speedLoss);

  if (speed < 0.1) {
    return { vx: 0, vz: 0, scraping: false };
  }

  const scale = newSpeed / speed;
  return {
    vx: scrubX * scale,
    vz: scrubZ * scale,
    scraping: Math.abs(normalDot) > 2,
  };
}

/** World edge (inset from ±HALF). */
export function resolveWorldBounds(
  x: number,
  z: number,
  vx: number,
  vz: number,
  r: number,
): { x: number; z: number; vx: number; vz: number } {
  const margin = 5;
  const lim = MAP.HALF - margin - r;
  let nx = x;
  let nz = z;
  let nvx = vx;
  let nvz = vz;
  if (nx < -lim) {
    nx = -lim;
    nvx = Math.abs(nvx) * 0.35;
  } else if (nx > lim) {
    nx = lim;
    nvx = -Math.abs(nvx) * 0.35;
  }
  if (nz < -lim) {
    nz = -lim;
    nvz = Math.abs(nvz) * 0.35;
  } else if (nz > lim) {
    nz = lim;
    nvz = -Math.abs(nvz) * 0.35;
  }
  return { x: nx, z: nz, vx: nvx, vz: nvz };
}

/**
 * Integrate position and resolve circle vs buildings + map bounds.
 * ROBUST: 8 passes to ensure car NEVER goes inside buildings.
 */
export function resolveVehicleCollisions(
  x: number,
  z: number,
  vx: number,
  vz: number,
  options?: { skipBuildings?: boolean; airY?: number; useWallScrub?: boolean },
): { x: number; z: number; vx: number; vz: number; wallScraping?: boolean } {
  /** Slightly tighter than old +0.5 so setback grass matches drivable area (still clears mesh). */
  const r = CAR.COLLISION_RADIUS + 0.28;
  let px = x;
  let pz = z;
  let pvx = vx;
  let pvz = vz;
  let wallScraping = false;
  const skipBuildings =
    options?.skipBuildings ||
    (options?.airY !== undefined && options.airY > AIRBORNE.CLEAR_BUILDINGS_Y);
  const boxes = skipBuildings ? [] : getBuildingAABBs();
  const useWallScrub = options?.useWallScrub !== false;

  for (let pass = 0; pass < 8; pass++) {
    for (const box of boxes) {
      const res = resolveCircleAABB(px, pz, r, box);
      if (!res) continue;
      px = res.x;
      pz = res.z;

      if (pass === 0) {
        if (useWallScrub) {
          const scrub = wallScrubVelocity(pvx, pvz, res.nx, res.nz);
          pvx = scrub.vx;
          pvz = scrub.vz;
          if (scrub.scraping) wallScraping = true;
        } else {
          const refl = reflectVelocity(
            pvx,
            pvz,
            res.nx,
            res.nz,
            PHYSICS.WALL_RESTITUTION,
          );
          pvx = refl.vx;
          pvz = refl.vz;
        }
      }
    }
  }

  for (const box of boxes) {
    const inside = px > box.minX && px < box.maxX && pz > box.minZ && pz < box.maxZ;
    if (inside) {
      const dxL = px - box.minX;
      const dxR = box.maxX - px;
      const dzB = pz - box.minZ;
      const dzT = box.maxZ - pz;
      const m = Math.min(dxL, dxR, dzB, dzT);
      if (m === dxL) px = box.minX - r - 1;
      else if (m === dxR) px = box.maxX + r + 1;
      else if (m === dzB) pz = box.minZ - r - 1;
      else pz = box.maxZ + r + 1;
      pvx *= 0.3;
      pvz *= 0.3;
    }
  }

  const edge = resolveWorldBounds(px, pz, pvx, pvz, r);
  return {
    x: edge.x,
    z: edge.z,
    vx: edge.vx,
    vz: edge.vz,
    wallScraping,
  };
}

/**
 * Nearest point on any building footprint → outward normal (from wall toward vehicle) and distance.
 */
export function getNearestBuildingOutwardNormal(
  x: number,
  z: number,
): { nx: number; nz: number; dist: number } | null {
  const buildings = getBuildingAABBs();
  let best: { nx: number; nz: number; dist: number } | null = null;

  for (const b of buildings) {
    const cx = (b.minX + b.maxX) / 2;
    const cz = (b.minZ + b.maxZ) / 2;
    const hw = (b.maxX - b.minX) / 2;
    const hd = (b.maxZ - b.minZ) / 2;

    const dx = x - cx;
    const dz = z - cz;
    const px = Math.max(-hw, Math.min(hw, dx));
    const pz = Math.max(-hd, Math.min(hd, dz));

    const nearX = cx + px;
    const nearZ = cz + pz;
    const dist = Math.hypot(x - nearX, z - nearZ);

    if (dist < 40 && (!best || dist < best.dist)) {
      const len = dist || 1;
      best = {
        nx: (x - nearX) / len,
        nz: (z - nearZ) / len,
        dist,
      };
    }
  }
  return best;
}

/**
 * Cop collision - push out of buildings but KEEP SPEED.
 * Only reduce velocity component going INTO the wall, preserve the rest.
 */
export function resolveCopVehicleCollisions(
  x: number,
  z: number,
  vx: number,
  vz: number,
  _goalX: number,
  _goalZ: number,
): { x: number; z: number; vx: number; vz: number } {
  const r = CAR.COLLISION_RADIUS + 0.28;
  let px = x;
  let pz = z;
  let nvx = vx;
  let nvz = vz;
  const boxes = getBuildingAABBs();

  for (let pass = 0; pass < 8; pass++) {
    for (const box of boxes) {
      const res = resolveCircleAABB(px, pz, r, box);
      if (res) {
        px = res.x;
        pz = res.z;
        
        if (pass === 0) {
          const dot = nvx * res.nx + nvz * res.nz;
          if (dot < 0) {
            nvx -= dot * res.nx;
            nvz -= dot * res.nz;
          }
        }
      }
    }
  }

  for (const box of boxes) {
    const inside = px > box.minX && px < box.maxX && pz > box.minZ && pz < box.maxZ;
    if (inside) {
      const dxL = px - box.minX;
      const dxR = box.maxX - px;
      const dzB = pz - box.minZ;
      const dzT = box.maxZ - pz;
      const m = Math.min(dxL, dxR, dzB, dzT);
      if (m === dxL) px = box.minX - r - 1;
      else if (m === dxR) px = box.maxX + r + 1;
      else if (m === dzB) pz = box.minZ - r - 1;
      else pz = box.maxZ + r + 1;
    }
  }

  const edge = resolveWorldBounds(px, pz, nvx, nvz, r);
  return { x: edge.x, z: edge.z, vx: edge.vx, vz: edge.vz };
}

export type NPCCollisionTarget = {
  x: number;
  z: number;
  vx: number;
  vz: number;
  collisionRadius: number;
  destroyed: boolean;
};

export type NPCCollisionResult = {
  x: number;
  z: number;
  vx: number;
  vz: number;
  hitNPC: boolean;
  impulse: number;
};

/**
 * Resolve player vehicle vs NPC traffic collision.
 * NPCs get pushed aside and player bounces off with momentum transfer.
 */
export function resolveNPCTrafficCollision(
  px: number,
  pz: number,
  pvx: number,
  pvz: number,
  npcs: NPCCollisionTarget[],
): NPCCollisionResult {
  const playerRadius = CAR.COLLISION_RADIUS;
  let x = px;
  let z = pz;
  let vx = pvx;
  let vz = pvz;
  let hitNPC = false;
  let totalImpulse = 0;

  for (const npc of npcs) {
    if (npc.destroyed) continue;

    const dx = x - npc.x;
    const dz = z - npc.z;
    const dist = Math.hypot(dx, dz);
    const minDist = playerRadius + npc.collisionRadius;

    if (dist < minDist && dist > 0.01) {
      hitNPC = true;
      const nx = dx / dist;
      const nz = dz / dist;
      const overlap = minDist - dist;

      x += nx * overlap * 0.7;
      z += nz * overlap * 0.7;

      const relVx = vx - npc.vx;
      const relVz = vz - npc.vz;
      const relDot = relVx * nx + relVz * nz;

      if (relDot < 0) {
        const impulse = Math.abs(relDot) * 0.65;
        totalImpulse = Math.max(totalImpulse, impulse);

        vx -= relDot * nx * 0.5;
        vz -= relDot * nz * 0.5;

        npc.vx += relDot * nx * 0.8;
        npc.vz += relDot * nz * 0.8;
      }
    }
  }

  return { x, z, vx, vz, hitNPC, impulse: totalImpulse };
}

/**
 * FORCE UNSTUCK - Call this every frame for cops.
 * If cop is near a building and moving slowly, push it away and
 * give it forward velocity (in the direction it's facing).
 */
export function forceUnstuckCop(
  cop: { x: number; z: number; vx: number; vz: number; heading: number },
  playerX: number,
  playerZ: number,
): void {
  const speed = Math.hypot(cop.vx, cop.vz);
  
  if (speed > 3) return;

  const near = getNearestBuildingOutwardNormal(cop.x, cop.z);
  
  if (!near || near.dist > 8) return;

  cop.x += near.nx * 2;
  cop.z += near.nz * 2;

  const dx = playerX - cop.x;
  const dz = playerZ - cop.z;
  const dist = Math.hypot(dx, dz);
  
  if (dist > 1) {
    cop.heading = Math.atan2(dx / dist, dz / dist);
  }

  const forwardX = Math.sin(cop.heading);
  const forwardZ = Math.cos(cop.heading);
  cop.vx = forwardX * 15;
  cop.vz = forwardZ * 15;
}
